import React, { useState, useEffect } from 'react'
import { 
  Check, X, Clock, AlertCircle, RefreshCw, Eye,
  DollarSign, ArrowUpRight, ArrowDownRight, User
} from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { supabase } from '../../services/supabase'
import { formatCurrency, formatDate } from '../../utils/formatters'
import toast from 'react-hot-toast'

const PendingApprovals = () => {
  const { profile } = useAuth()
  const [pending, setPending] = useState([])
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState(null)
  const [selected, setSelected] = useState([])
  const [selectedTransaction, setSelectedTransaction] = useState(null)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [rejectionReason, setRejectionReason] = useState('')
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [rejectingId, setRejectingId] = useState(null)

  useEffect(() => {
    loadPending()
    
    // Real-time subscription for new pending transactions
    const subscription = supabase
      .channel('pending_transactions')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'transactions', filter: 'status=eq.pending' },
        (payload) => {
          console.log('Transaction change:', payload)
          loadPending()
          if (payload.eventType === 'INSERT') {
            toast.success('New transaction pending approval!')
          }
        }
      )
      .subscribe()

    // Refresh every 10 seconds
    const interval = setInterval(loadPending, 10000)
    
    return () => {
      subscription.unsubscribe()
      clearInterval(interval)
    }
  }, [])

  const loadPending = async () => {
    try {
      // Get pending transactions
      const { data: transactionsData, error: txError } = await supabase
        .from('transactions')
        .select('*')
        .eq('status', 'pending')
        .order('created_at', { ascending: true })

      if (txError) throw txError

      // Get user info for each transaction
      if (transactionsData && transactionsData.length > 0) {
        const userIds = [...new Set(transactionsData.map(t => t.user_id).filter(Boolean))]
        
        const { data: usersData, error: usersError } = await supabase
          .from('users')
          .select('id, username, email, full_name, name, balance')
          .in('id', userIds)

        if (usersError) throw usersError

        // Merge user data with transactions
        const transactionsWithUsers = transactionsData.map(tx => ({
          ...tx,
          users: usersData?.find(u => u.id === tx.user_id) || null
        }))

        setPending(transactionsWithUsers)
      } else {
        setPending([])
      }
    } catch (error) {
      console.error('Error loading pending transactions:', error)
      toast.error('Failed to load pending transactions')
    } finally {
      setLoading(false)
    }
  }

  const handleApprove = async (transactionId) => {
    setProcessing(transactionId)
    
    try {
      const transaction = pending.find(t => t.id === transactionId)
      if (!transaction) throw new Error('Transaction not found')

      const user = transaction.users
      if (!user) throw new Error('User not found')

      // Calculate new balance
      let newBalance = parseFloat(user.balance || 0)
      const amount = parseFloat(transaction.amount)

      if (transaction.type === 'deposit') {
        newBalance += amount
      } else if (transaction.type === 'withdrawal') {
        newBalance -= amount
        if (newBalance < 0) {
          throw new Error('Insufficient balance')
        }
      }

      // Update transaction status
      const { error: txError } = await supabase
        .from('transactions')
        .update({
          status: 'completed',
          approved_by: profile.id,
          approved_at: new Date().toISOString(),
          balance_before: user.balance,
          balance_after: newBalance,
          updated_at: new Date().toISOString()
        })
        .eq('id', transactionId)

      if (txError) throw txError

      // Update user balance
      const { error: balanceError } = await supabase
        .from('users')
        .update({ balance: newBalance })
        .eq('id', user.id)

      if (balanceError) throw balanceError

      // Log activity
      await supabase
        .from('activity_logs')
        .insert({
          user_id: profile.id,
          action: 'transaction_approved',
          details: `Approved ${transaction.type} of ${formatCurrency(amount)} for ${user.email}`
        })

      // Send notification to user
      await supabase
        .from('notifications')
        .insert({
          user_id: user.id,
          type: 'transaction_approved',
          title: 'Transaction Approved',
          message: `Your ${transaction.type} of ${formatCurrency(amount)} has been approved.`
        })

      toast.success('Transaction approved successfully!')
      loadPending()
    } catch (error) {
      console.error('Error approving transaction:', error)
      toast.error(error.message || 'Failed to approve transaction')
    } finally {
      setProcessing(null)
    }
  }

  const handleReject = async () => {
    if (!rejectingId || !rejectionReason.trim()) {
      toast.error('Please provide a rejection reason')
      return
    }

    setProcessing(rejectingId)
    
    try {
      const transaction = pending.find(t => t.id === rejectingId)
      if (!transaction) throw new Error('Transaction not found')

      // Update transaction status
      const { error: txError } = await supabase
        .from('transactions')
        .update({
          status: 'failed',
          approved_by: profile.id,
          approved_at: new Date().toISOString(),
          description: `Rejected: ${rejectionReason}`,
          updated_at: new Date().toISOString()
        })
        .eq('id', rejectingId)

      if (txError) throw txError

      // Log activity
      await supabase
        .from('activity_logs')
        .insert({
          user_id: profile.id,
          action: 'transaction_rejected',
          details: `Rejected ${transaction.type} of ${formatCurrency(transaction.amount)} for ${transaction.users?.email}. Reason: ${rejectionReason}`
        })

      // Send notification to user
      await supabase
        .from('notifications')
        .insert({
          user_id: transaction.user_id,
          type: 'transaction_rejected',
          title: 'Transaction Rejected',
          message: `Your ${transaction.type} of ${formatCurrency(transaction.amount)} was rejected. Reason: ${rejectionReason}`
        })

      toast.success('Transaction rejected')
      setShowRejectModal(false)
      setRejectionReason('')
      setRejectingId(null)
      loadPending()
    } catch (error) {
      console.error('Error rejecting transaction:', error)
      toast.error(error.message || 'Failed to reject transaction')
    } finally {
      setProcessing(null)
    }
  }

  const handleBulkApprove = async () => {
    if (selected.length === 0) return
    if (!confirm(`Approve ${selected.length} transaction(s)?`)) return

    setLoading(true)
    let successCount = 0

    try {
      for (const txId of selected) {
        try {
          await handleApprove(txId)
          successCount++
        } catch (error) {
          console.error(`Failed to approve transaction ${txId}:`, error)
        }
      }

      toast.success(`${successCount} of ${selected.length} transaction(s) approved!`)
      setSelected([])
      loadPending()
    } catch (error) {
      console.error('Error in bulk approve:', error)
      toast.error('Some transactions failed to approve')
    } finally {
      setLoading(false)
    }
  }

  const toggleSelect = (id) => {
    setSelected(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    )
  }

  const toggleSelectAll = () => {
    setSelected(prev =>
      prev.length === pending.length ? [] : pending.map(t => t.id)
    )
  }

  const handleViewDetails = (transaction) => {
    setSelectedTransaction(transaction)
    setShowDetailsModal(true)
  }

  const openRejectModal = (transactionId) => {
    setRejectingId(transactionId)
    setShowRejectModal(true)
  }

  if (loading && pending.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-3 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
            Pending Approvals
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1">
            {pending.length} transaction{pending.length !== 1 ? 's' : ''} awaiting approval
          </p>
        </div>
        
        <div className="flex gap-2 w-full sm:w-auto">
          <button
            onClick={loadPending}
            className="flex-1 sm:flex-none px-4 py-2 border-2 border-gray-300 dark:border-dark-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 flex items-center justify-center gap-2 transition-colors"
          >
            <RefreshCw className="w-5 h-5" />
            Refresh
          </button>
          
          {selected.length > 0 && (
            <button
              onClick={handleBulkApprove}
              className="flex-1 sm:flex-none px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2 transition-colors"
            >
              <Check className="w-5 h-5" />
              Approve {selected.length}
            </button>
          )}
        </div>
      </div>

      {/* Alert */}
      {pending.length > 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 flex gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-yellow-800 dark:text-yellow-300">
            These transactions will update user balances once approved. Review carefully before approving.
          </p>
        </div>
      )}

      {/* Transactions Table/Cards */}
      {pending.length === 0 ? (
        <div className="bg-white dark:bg-dark-800 rounded-lg shadow-lg p-8 sm:p-12 text-center">
          <Clock className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            No Pending Transactions
          </h3>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
            All transactions have been processed. Check back later!
          </p>
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="hidden lg:block bg-white dark:bg-dark-800 rounded-lg shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-700">
                <thead className="bg-gray-50 dark:bg-dark-900">
                  <tr>
                    <th className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selected.length === pending.length}
                        onChange={toggleSelectAll}
                        className="rounded"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Current Balance
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      New Balance
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Requested
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-dark-700">
                  {pending.map((transaction) => {
                    const currentBalance = parseFloat(transaction.users?.balance || 0)
                    const amount = parseFloat(transaction.amount)
                    const newBalance = transaction.type === 'deposit'
                      ? currentBalance + amount
                      : currentBalance - amount

                    return (
                      <tr key={transaction.id} className="hover:bg-gray-50 dark:hover:bg-dark-700">
                        <td className="px-4 py-4">
                          <input
                            type="checkbox"
                            checked={selected.includes(transaction.id)}
                            onChange={() => toggleSelect(transaction.id)}
                            className="rounded"
                          />
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                              <User className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">
                                {transaction.users?.full_name || transaction.users?.name || transaction.users?.username}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {transaction.users?.email}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs rounded-full inline-flex items-center gap-1 ${
                            transaction.type === 'deposit' 
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                              : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                          }`}>
                            {transaction.type === 'deposit' ? <ArrowDownRight className="w-3 h-3" /> : <ArrowUpRight className="w-3 h-3" />}
                            {transaction.type}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`font-semibold ${
                            transaction.type === 'deposit' ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {transaction.type === 'deposit' ? '+' : '-'}
                            {formatCurrency(amount)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-gray-900 dark:text-white font-medium">
                          {formatCurrency(currentBalance)}
                        </td>
                        <td className="px-6 py-4">
                          <span className="font-semibold text-gray-900 dark:text-white">
                            {formatCurrency(newBalance)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                          {formatDate(transaction.created_at)}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleViewDetails(transaction)}
                              className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleApprove(transaction.id)}
                              disabled={processing === transaction.id}
                              className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-1 transition-colors"
                            >
                              <Check className="w-4 h-4" />
                              Approve
                            </button>
                            <button
                              onClick={() => openRejectModal(transaction.id)}
                              disabled={processing === transaction.id}
                              className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-1 transition-colors"
                            >
                              <X className="w-4 h-4" />
                              Reject
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden space-y-4">
            {pending.map((transaction) => {
              const currentBalance = parseFloat(transaction.users?.balance || 0)
              const amount = parseFloat(transaction.amount)
              const newBalance = transaction.type === 'deposit'
                ? currentBalance + amount
                : currentBalance - amount

              return (
                <div key={transaction.id} className="bg-white dark:bg-dark-800 rounded-lg shadow-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={selected.includes(transaction.id)}
                        onChange={() => toggleSelect(transaction.id)}
                        className="rounded mt-1"
                      />
                      <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                        <User className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white truncate">
                          {transaction.users?.full_name || transaction.users?.name || transaction.users?.username}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {transaction.users?.email}
                        </p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full flex-shrink-0 ${
                      transaction.type === 'deposit'
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                        : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                    }`}>
                      {transaction.type}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400">Amount</label>
                      <p className={`font-semibold text-sm ${
                        transaction.type === 'deposit' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {transaction.type === 'deposit' ? '+' : '-'}{formatCurrency(amount)}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400">Current Balance</label>
                      <p className="font-medium text-sm text-gray-900 dark:text-white">
                        {formatCurrency(currentBalance)}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400">New Balance</label>
                      <p className="font-semibold text-sm text-gray-900 dark:text-white">
                        {formatCurrency(newBalance)}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 dark:text-gray-400">Requested</label>
                      <p className="text-sm text-gray-900 dark:text-white">
                        {formatDate(transaction.created_at)}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleViewDetails(transaction)}
                      className="flex-1 px-3 py-2 text-sm border-2 border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 flex items-center justify-center gap-2 transition-colors"
                    >
                      <Eye className="w-4 h-4" />
                      View
                    </button>
                    <button
                      onClick={() => handleApprove(transaction.id)}
                      disabled={processing === transaction.id}
                      className="flex-1 px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
                    >
                      <Check className="w-4 h-4" />
                      Approve
                    </button>
                    <button
                      onClick={() => openRejectModal(transaction.id)}
                      disabled={processing === transaction.id}
                      className="flex-1 px-3 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
                    >
                      <X className="w-4 h-4" />
                      Reject
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-800 rounded-xl max-w-2xl w-full p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                Transaction Details
              </h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-xs text-gray-500 dark:text-gray-400">Transaction ID</label>
                <p className="text-sm font-mono text-gray-900 dark:text-white break-all">
                  {selectedTransaction.id}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400">Type</label>
                  <p className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                    {selectedTransaction.type}
                  </p>
                </div>
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400">Amount</label>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">
                    {formatCurrency(selectedTransaction.amount)}
                  </p>
                </div>
              </div>

              {selectedTransaction.description && (
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400">Description</label>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {selectedTransaction.description}
                  </p>
                </div>
              )}

              <div>
                <label className="text-xs text-gray-500 dark:text-gray-400">User</label>
                <div className="mt-2 p-3 bg-gray-50 dark:bg-dark-700 rounded-lg">
                  <p className="font-medium text-gray-900 dark:text-white">
                    {selectedTransaction.users?.full_name || selectedTransaction.users?.name || selectedTransaction.users?.username}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {selectedTransaction.users?.email}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-dark-700">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="w-full px-4 py-2 bg-gray-200 dark:bg-dark-700 text-gray-900 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-dark-600 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-800 rounded-xl max-w-md w-full p-4 sm:p-6">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-4">
              Reject Transaction
            </h3>

            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4 flex gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800 dark:text-red-300">
                This action cannot be undone. The user will be notified.
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Rejection Reason *
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Explain why this transaction is being rejected..."
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white"
                rows="4"
                required
              />
            </div>

            <div className="flex flex-col-reverse sm:flex-row gap-3">
              <button
                onClick={() => {
                  setShowRejectModal(false)
                  setRejectionReason('')
                  setRejectingId(null)
                }}
                className="flex-1 px-4 py-2 border-2 border-gray-300 dark:border-dark-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={processing || !rejectionReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {processing ? 'Processing...' : 'Confirm Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PendingApprovals